<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import OxygenChart from '@/components/charts/OxygenChart.vue'
import GrowthChart from '@/components/charts/GrowthChart.vue'
import TemperatureOxygenChart from '@/components/charts/TemperatureOxygenChart.vue'
import { useDeviceStore } from '@/views/Devices/store'
import { useMeasurementsStore } from '@/views/DeviceSamples/store'
import { useDeviceSensorManager, type DeviceSensorData } from '@/composables/useDeviceSensorManager'
import type { Device } from '@/views/Devices/models/device'
import type { DeviceSample } from '@/views/DeviceSamples/models/deviceSample'
import { useDeviceLoader } from '@/composables/useDeviceLoader'

// Stores
const deviceStore = useDeviceStore()
const measurementsStore = useMeasurementsStore()
const sensorManager = useDeviceSensorManager()
const deviceLoader = useDeviceLoader()

// Estados reactivos
const devices = ref<Device[]>([])
const selectedDeviceId = ref<string>('')
const allMeasurements = ref<DeviceSample[]>([])
const selectedDeviceSensorData = ref<DeviceSensorData>({
  temperatureData: [],
  oxygenData: [],
  batteryData: [],
  temperatureOxygenData: [],
  lastValues: {}
})
const selectedDeviceGrowthData = ref<Array<{ date: string, growth: number, newRoots: number }>>([])

// Estados de carga
const loading = ref(false)
const error = ref<string | null>(null)

// Computed properties para estadísticas del dashboard
const dashboardStats = computed(() => {
  const totalDevices = devices.value.length
  const activeDevices = devices.value.filter(d => {
    const metadata = typeof d.metadata === 'string' && d.metadata ? JSON.parse(d.metadata) : d.metadata || {}
    return metadata.status === 'connected'
  }).length
  const offlineDevices = totalDevices - activeDevices
  const alertDevices = devices.value.filter(d => {
    const metadata = typeof d.metadata === 'string' && d.metadata ? JSON.parse(d.metadata) : d.metadata || {}
    return (metadata.batteryLevel && metadata.batteryLevel < 20) ||
      metadata.status === 'disconnected' ||
      metadata.status === 'maintenance'
  }).length

  // Calcular mediciones de hoy
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const measurementsToday = allMeasurements.value.filter(m =>
    new Date(m.createdAt).getTime() >= today.getTime()
  ).length

  // Calcular estadísticas de raíces
  const managedMeasurements = allMeasurements.value.filter(m => m.managed)
  const totalRootsDetected = managedMeasurements.reduce((sum, m) => sum + m.newRootsCount, 0)
  const totalNewRootsToday = allMeasurements.value
    .filter(m => m.managed && new Date(m.createdAt).getTime() >= today.getTime())
    .reduce((sum, m) => sum + m.newRootsCount, 0)

  // Calcular promedios
  const averageGrowthPercentage = managedMeasurements.length > 0
    ? managedMeasurements.reduce((sum, m) => sum + m.growthPercent, 0) / managedMeasurements.length
    : 0

  // Datos del dispositivo seleccionado para promedios de sensores
  const selectedDevice = devices.value.find(d => d.id === selectedDeviceId.value)
  const selectedDeviceMetadata = selectedDevice && typeof selectedDevice.metadata === 'string'
    ? JSON.parse(selectedDevice.metadata)
    : selectedDevice?.metadata || {}

  const averageHumidity = selectedDeviceSensorData.value.lastValues.oxygen ?
    Math.round(selectedDeviceSensorData.value.lastValues.oxygen * 10) / 10 : 65
  const averageOxygen = selectedDeviceSensorData.value.lastValues.oxygen ?
    Math.round(selectedDeviceSensorData.value.lastValues.oxygen * 10) / 10 : 18
  const averageBatteryLevel = selectedDeviceSensorData.value.lastValues.batteryLevel ?
    Math.round(selectedDeviceSensorData.value.lastValues.batteryLevel) : selectedDeviceMetadata?.batteryLevel || 75

  return {
    totalDevices,
    activeDevices,
    offlineDevices,
    alertDevices,
    measurementsToday,
    totalRootsDetected,
    averageGrowthPercentage: Math.round(averageGrowthPercentage * 10) / 10,
    averageHumidity,
    averageOxygen,
    averageBatteryLevel,
    totalNewRootsToday
  }
})

// Device status distribution computed
const deviceStatusCounts = computed(() => {
  return [
    { status: 'En línea', count: dashboardStats.value.activeDevices, color: '#7ED957' },
    { status: 'Advertencia', count: dashboardStats.value.alertDevices, color: '#FFA726' },
    { status: 'Desconectado', count: dashboardStats.value.offlineDevices, color: '#FF6B6B' }
  ]
})

// Fotorizotron devices data with calculated fields
const fotorizotronDevices = computed(() => {
  return devices.value.map(device => {
    // Parse metadata safely
    const metadata = typeof device.metadata === 'string' && device.metadata
      ? JSON.parse(device.metadata)
      : device.metadata || {}

    const batteryLevel = metadata?.batteryLevel || 75
    const status = metadata?.status || 'disconnected'
    const isOnline = status === 'connected'
    const isLowBattery = batteryLevel < 20

    // Calculate operating days
    const operatingDays = device.initDate ?
      Math.floor((Date.now() - new Date(device.initDate).getTime()) / (1000 * 60 * 60 * 24)) : 0

    // Get device deviceSamples for growth and root calculations
    const deviceMeasurements = allMeasurements.value.filter(m => m.deviceId === device.id)
    const managedMeasurements = deviceMeasurements.filter(m => m.managed)

    const totalRoots = managedMeasurements.reduce((sum, m) => sum + m.newRootsCount, 0)
    const todayMeasurements = managedMeasurements.filter(m => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return new Date(m.createdAt).getTime() >= today.getTime()
    })
    const newRootsToday = todayMeasurements.reduce((sum, m) => sum + m.newRootsCount, 0)

    const averageGrowth = managedMeasurements.length > 0
      ? managedMeasurements.reduce((sum, m) => sum + m.growthPercent, 0) / managedMeasurements.length
      : 0

    // Get real sensor data for selected device
    let humidity = 65 // default
    let oxygen = 18 // default
    if (device.id === selectedDeviceId.value) {
      humidity = selectedDeviceSensorData.value.lastValues.oxygen ?
        Math.round(selectedDeviceSensorData.value.lastValues.oxygen * 10) / 10 : 65
      oxygen = selectedDeviceSensorData.value.lastValues.oxygen ?
        Math.round(selectedDeviceSensorData.value.lastValues.oxygen * 10) / 10 : 18
    }

    return {
      ...device,
      operatingDays,
      growth: Math.round(averageGrowth * 10) / 10,
      humidity,
      oxygen,
      totalRoots,
      newRoots: newRootsToday,
      status: isOnline ? 'online' : isLowBattery ? 'error' : 'warning',
      statusColor: isOnline ? 'success' : isLowBattery ? 'error' : 'warning',
      location: `Ubicación ${device.locationId}`, // Temporary display until we have location names
      batteryLevel,
      metadata
    }
  })
})

// Recent root detections computed
const recentRootDetections = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return allMeasurements.value
    .filter(m => m.managed && new Date(m.createdAt).getTime() >= today.getTime())
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 3)
    .map(m => ({
      deviceId: m.deviceId,
      deviceName: devices.value.find(d => d.id === m.deviceId)?.name || 'Dispositivo desconocido',
      newRoots: m.newRootsCount,
      growthPercentage: m.growthPercent,
      timestamp: m.createdAt.toISOString(),
      healthScore: m.rootHealthScore
    }))
})

// Notifications (keeping static for now as requested)
const notifications = ref([
  { message: 'Alerta: Batería baja en dispositivo', time: '15m', type: 'error' },
  { message: 'Medición pendiente de análisis', time: '1h', type: 'warning' },
  { message: 'Nuevas raíces detectadas', time: '2h', type: 'success' },
  { message: 'Sistema funcionando correctamente', time: '1d', type: 'info' }
])

// Sample data for charts based on selected device
const sampleSensorDataForCharts = computed(() => {
  if (!selectedDeviceSensorData.value.temperatureOxygenData.length) {
    return []
  }
  return selectedDeviceSensorData.value.temperatureOxygenData.map(data => ({
    timestamp: data.timestamp,
    humidity: 65, // Default value since we don't have humidity sensor data yet
    oxygen: data.oxygen,
    temperature: data.temperature,
    batteryLevel: selectedDeviceSensorData.value.batteryData.find(b =>
      b.timestamp === data.timestamp
    )?.batteryLevel || 75
  }))
})

const sampleGrowthDataForChart = computed(() => {
  return selectedDeviceGrowthData.value
})

// Functions to load real data
const loadDevices = async () => {
  try {
    loading.value = true

    // Usar carga condicional por rol
    await deviceLoader.loadDevicesByRole()
    devices.value = deviceStore.devices

    // Seleccionar primer device por defecto
    if (devices.value.length > 0 && !selectedDeviceId.value) {
      selectedDeviceId.value = devices.value[0].id
    }

  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Error cargando dispositivos'
    console.error('Error loading devices:', err)
  } finally {
    loading.value = false
  }
}

const loadMeasurements = async () => {
  try {
    await measurementsStore.getMeasurements()
    allMeasurements.value = measurementsStore.deviceSamples
  } catch (err) {
    console.error('Error loading deviceSamples:', err)
  }
}

const loadSelectedDeviceMeasurements = async () => {
  if (!selectedDeviceId.value) return

  try {
    loading.value = true
    const [sensorData, growthData] = await Promise.all([
      sensorManager.loadDeviceSensorData(selectedDeviceId.value),
      sensorManager.loadGrowthData(selectedDeviceId.value)
    ])

    selectedDeviceSensorData.value = sensorData
    selectedDeviceGrowthData.value = growthData
  } catch (err) {
    console.error('Error loading selected device data:', err)
  } finally {
    loading.value = false
  }
}

const loadAllData = async () => {
  await Promise.all([
    loadDevices(),
    loadMeasurements()
  ])

  if (selectedDeviceId.value) {
    await loadSelectedDeviceMeasurements()
  }
}

// Auto-refresh interval
const refreshInterval = ref<ReturnType<typeof setInterval> | null>(null)

// Watch for device selection changes
watch(selectedDeviceId, (newDeviceId) => {
  if (newDeviceId) {
    loadSelectedDeviceMeasurements()
  }
})

// Setup auto-refresh every 60 seconds
const setupAutoRefresh = () => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
  }

  refreshInterval.value = setInterval(async () => {
    console.log('Auto-refreshing data...')
    await loadAllData()
  }, 60000) // 60 seconds
}

// Clear auto-refresh
const clearAutoRefresh = () => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
    refreshInterval.value = null
  }
}

// Lifecycle
onMounted(() => {
  loadAllData()
  setupAutoRefresh()
})

onUnmounted(() => {
  clearAutoRefresh()
})

// Time period toggle
const timePeriod = ref('weekly')

// Format percentage with color
const getPercentageColor = (value: number, type = 'growth') => {
  if (type === 'growth') {
    return value >= 3 ? 'success' : value >= 2 ? 'warning' : 'error'
  } else if (type === 'battery') {
    return value >= 70 ? 'success' : value >= 40 ? 'warning' : 'error'
  }
  return 'default'
}

// Calculate health score color
const getHealthScoreColor = (score: number) => {
  return score >= 7 ? 'success' : score >= 5 ? 'warning' : 'error'
}
</script>

<template>
  <v-container fluid>
    <!-- Header Section -->
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 font-weight-bold">Dashboard Fotorizotron</h1>
            <p class="text-body-1 text-medium-emphasis">
              Monitoreo en tiempo real de dispositivos de análisis de raíces
              <span v-if="selectedDeviceId" class="text-primary ml-2">
                • Mostrando: {{devices.find(d => d.id === selectedDeviceId)?.name}}
              </span>
            </p>
          </div>
          <div class="d-flex align-center gap-3">
            <!-- Device Selector -->
            <v-select v-model="selectedDeviceId" :items="devices" item-title="name" item-value="id"
              label="Seleccionar Dispositivo" density="comfortable" variant="outlined"
              prepend-inner-icon="mdi-camera-outline" class="device-selector" style="min-width: 250px;"
              :loading="loading" @update:model-value="loadSelectedDeviceMeasurements">
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props" :title="item.raw.name" :subtitle="item.raw.description">
                  <template v-slot:prepend>
                    <v-avatar color="primary" size="32">
                      <v-icon color="white" size="16">mdi-camera-outline</v-icon>
                    </v-avatar>
                  </template>
                  <template v-slot:append>
                    <v-chip
                      :color="((typeof item.raw.metadata === 'string' ? JSON.parse(item.raw.metadata) : item.raw.metadata) || {}).status === 'connected' ? 'success' : 'warning'"
                      size="x-small" text-color="white">
                      {{ ((typeof item.raw.metadata === 'string' ? JSON.parse(item.raw.metadata) : item.raw.metadata) ||
                        {}).status === 'connected' ? 'En línea' : 'Desconectado' }}
                    </v-chip>
                  </template>
                </v-list-item>
              </template>
            </v-select>

            <!-- Auto-refresh indicator -->
            <v-chip color="info" size="small" prepend-icon="mdi-refresh">
              Auto-actualización 60s
            </v-chip>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row>
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" flat elevation="2">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#F5F3FF" size="48" class="mr-3">
                <v-icon color="#8465FF" size="24">mdi-camera-outline</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Total Dispositivos</div>
                <div class="text-h4 font-weight-bold">{{ dashboardStats.totalDevices }}</div>
                <div class="text-caption text-success">{{ dashboardStats.activeDevices }} activos</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear model-value="100" height="6" color="#8465FF" rounded></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" flat elevation="2">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#EAF6FF" size="48" class="mr-3">
                <v-icon color="#4CB3FF" size="24">mdi-wifi</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Dispositivos Activos</div>
                <div class="text-h4 font-weight-bold">{{ dashboardStats.activeDevices }}</div>
                <div class="text-caption text-success">{{
                  Math.round((dashboardStats.activeDevices / dashboardStats.totalDevices) * 100) }}% en línea</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear :model-value="(dashboardStats.activeDevices / dashboardStats.totalDevices) * 100"
              height="6" color="#4CB3FF" rounded></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" flat elevation="2">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#F0FBE6" size="48" class="mr-3">
                <v-icon color="#7ED957" size="24">mdi-sprout-outline</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Raíces Detectadas</div>
                <div class="text-h4 font-weight-bold">{{ dashboardStats.totalRootsDetected }}</div>
                <div class="text-caption text-success">+{{ dashboardStats.totalNewRootsToday }} hoy</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear model-value="85" height="6" color="#7ED957" rounded></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" flat elevation="2">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#FFF8EA" size="48" class="mr-3">
                <v-icon color="#FFA726" size="24">mdi-chart-line</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Crecimiento Promedio</div>
                <div class="text-h4 font-weight-bold">{{ dashboardStats.averageGrowthPercentage }}%</div>
                <div class="text-caption text-success">↑ 0.3% vs ayer</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear :model-value="dashboardStats.averageGrowthPercentage * 20" height="6" color="#FFA726"
              rounded></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content -->
    <v-row class="mt-6">
      <!-- Devices Overview -->
      <v-col cols="12" lg="8">
        <v-card flat class="rounded-lg" elevation="2">
          <v-card-title class="d-flex justify-space-between align-center px-6 py-4">
            <div class="text-h6 font-weight-bold">Dispositivos Fotorizotron</div>
            <v-btn variant="text" color="primary" prepend-icon="mdi-eye">Ver todos</v-btn>
          </v-card-title>

          <v-card-text class="px-6">
            <v-table>
              <thead>
                <tr>
                  <th class="text-left">Dispositivo</th>
                  <th class="text-left">Ubicación</th>
                  <th class="text-left">Crecimiento</th>
                  <th class="text-left">Sensores</th>
                  <th class="text-left">Raíces</th>
                  <th class="text-left">Estado</th>
                  <th class="text-left">Batería</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="device in fotorizotronDevices" :key="device.id" class="device-row">
                  <td>
                    <div class="d-flex align-center">
                      <v-avatar size="40" color="#EAF6FF" class="mr-3">
                        <v-icon color="#4CB3FF" size="20">mdi-camera-outline</v-icon>
                      </v-avatar>
                      <div>
                        <div class="font-weight-medium">{{ device.name }}</div>
                        <div class="text-caption text-medium-emphasis">{{ device.id }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>{{ device.location }}</div>
                    <div class="text-caption text-medium-emphasis">{{ device.operatingDays }} días activo</div>
                  </td>
                  <td>
                    <v-chip size="small" :color="getPercentageColor(device.growth)" text-color="white">
                      {{ device.growth }}%
                    </v-chip>
                  </td>
                  <td>
                    <div class="text-body-2">H: {{ device.humidity }}%</div>
                    <div class="text-body-2">O₂: {{ device.oxygen }}%</div>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <v-icon size="16" color="purple" class="mr-1">mdi-sprout</v-icon>
                      <span class="font-weight-medium">{{ device.totalRoots }}</span>
                      <span class="text-caption ml-1">(+{{ device.newRoots }})</span>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <v-icon
                        :color="device.status === 'online' ? 'success' : device.status === 'warning' ? 'warning' : 'error'"
                        size="12" class="mr-2">
                        mdi-circle
                      </v-icon>
                      <v-chip size="small" :color="device.statusColor" text-color="white">
                        {{ device.status === 'online' ? 'En línea' : device.status === 'warning' ? 'Advertencia' :
                          'Desconectado' }}
                      </v-chip>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <v-icon :color="getPercentageColor(device.batteryLevel, 'battery')" size="16" class="mr-1">
                        mdi-battery
                      </v-icon>
                      <span
                        :class="getPercentageColor(device.batteryLevel, 'battery') === 'error' ? 'text-error' :
                          getPercentageColor(device.batteryLevel, 'battery') === 'warning' ? 'text-warning' : 'text-success'">
                        {{ device.batteryLevel }}%
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Real-time Sensor Data Charts -->
        <v-row class="mt-6">
          <v-col cols="12" md="6">
            <v-card flat elevation="2" class="rounded-lg">
              <v-card-title class="d-flex justify-space-between align-center px-4 py-3">
                <span class="text-h6 font-weight-bold">Oxígeno Actual</span>
                <v-chip v-if="selectedDeviceId" color="info" size="x-small" text-color="white">
                  {{devices.find(d => d.id === selectedDeviceId)?.name}}
                </v-chip>
              </v-card-title>
              <v-card-text class="px-4">
                <div class="text-center mb-3">
                  <div class="text-h4 font-weight-bold text-info">
                    {{ selectedDeviceSensorData.lastValues?.oxygen || dashboardStats.averageOxygen }}%
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ selectedDeviceId ? 'Dispositivo seleccionado' : 'Promedio del sistema' }}
                  </div>
                </div>
                <OxygenChart :oxygen="selectedDeviceSensorData.lastValues?.oxygen || dashboardStats.averageOxygen" />
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card flat elevation="2" class="rounded-lg">
              <v-card-title class="d-flex justify-space-between align-center px-4 py-3">
                <span class="text-h6 font-weight-bold">Crecimiento Semanal</span>
                <v-chip v-if="selectedDeviceId" color="success" size="x-small" text-color="white">
                  {{devices.find(d => d.id === selectedDeviceId)?.name}}
                </v-chip>
              </v-card-title>
              <v-card-text class="px-4">
                <div v-if="!selectedDeviceId || sampleGrowthDataForChart.length === 0" class="text-center py-8">
                  <v-icon size="48" color="grey-lighten-2">mdi-chart-line</v-icon>
                  <div class="text-body-2 text-medium-emphasis mt-2">
                    {{ selectedDeviceId ? 'Sin datos de crecimiento' : 'Selecciona un dispositivo' }}
                  </div>
                </div>
                <GrowthChart v-else :growth-data="sampleGrowthDataForChart" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Combined Trend Chart -->
        <v-card flat class="rounded-lg mt-6" elevation="2">
          <v-card-title class="d-flex justify-space-between align-center px-6 py-4">
            <div class="d-flex align-center">
              <span class="text-h6 font-weight-bold">Tendencias de Sensores</span>
              <v-chip v-if="selectedDeviceId" color="primary" size="small" class="ml-3" text-color="white">
                {{devices.find(d => d.id === selectedDeviceId)?.name}}
              </v-chip>
            </div>
            <div class="d-flex align-center">
              <v-btn-toggle v-model="timePeriod" mandatory density="comfortable" color="primary" class="mr-3">
                <v-btn value="daily" size="small">Diario</v-btn>
                <v-btn value="weekly" size="small">Semanal</v-btn>
                <v-btn value="monthly" size="small">Mensual</v-btn>
              </v-btn-toggle>
            </div>
          </v-card-title>

          <v-card-text class="px-6">
            <div class="mb-4">
              <div class="text-body-2 text-medium-emphasis">
                {{selectedDeviceId
                  ? `Evolución de sensores del dispositivo ${devices.find(d => d.id === selectedDeviceId)?.name}`
                  : 'Selecciona un dispositivo para ver las tendencias de sensores'
                }}
              </div>
            </div>

            <div v-if="!selectedDeviceId || sampleSensorDataForCharts.length === 0" class="text-center py-12">
              <v-icon size="64" color="grey-lighten-2">mdi-chart-line-variant</v-icon>
              <div class="text-h6 text-medium-emphasis mt-4">
                {{ selectedDeviceId ? 'Sin datos de sensores disponibles' : 'Selecciona un dispositivo' }}
              </div>
              <div class="text-body-2 text-medium-emphasis mt-2">
                {{ selectedDeviceId ? 'Los datos se actualizarán automáticamente cada 60 segundos' : 'Usa el selector de dispositivos arriba' }}
              </div>
            </div>

            <TemperatureOxygenChart v-else :sensor-data="selectedDeviceSensorData.temperatureOxygenData" title="Temperatura vs Oxígeno" height="300px" />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right Column -->
      <v-col cols="12" lg="4">
        <!-- Device Status Distribution -->
        <v-card flat class="rounded-lg" elevation="2">
          <v-card-title class="d-flex justify-space-between align-center px-4 py-4">
            <div class="text-h6 font-weight-bold">Estado de la Red</div>
            <v-btn icon variant="text" size="small">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="px-4">
            <v-list>
              <v-list-item v-for="(status, i) in deviceStatusCounts" :key="i" class="px-0 mb-2">
                <template v-slot:prepend>
                  <v-avatar :color="status.color" size="12" class="mr-3"></v-avatar>
                </template>
                <v-list-item-title class="text-body-2">{{ status.status }}</v-list-item-title>
                <template v-slot:append>
                  <div class="d-flex align-center">
                    <div class="text-h6 font-weight-bold mr-2">{{ status.count }}</div>
                    <div class="text-caption text-medium-emphasis">
                      {{ Math.round((status.count / dashboardStats.totalDevices) * 100) }}%
                    </div>
                  </div>
                </template>
              </v-list-item>
            </v-list>

            <v-sheet height="120" class="mt-4 chart-placeholder d-flex justify-center align-center rounded">
              <div class="text-center">
                <v-icon size="32" color="grey-lighten-1">mdi-chart-donut</v-icon>
                <div class="text-caption text-center mt-1">Distribución de estados</div>
              </div>
            </v-sheet>
          </v-card-text>
        </v-card>

        <!-- Recent Root Detections -->
        <v-card flat class="rounded-lg mt-6" elevation="2">
          <v-card-title class="d-flex justify-space-between align-center px-4 py-4">
            <div class="text-h6 font-weight-bold">Detecciones Recientes</div>
            <v-chip color="success" size="small" text-color="white">{{ dashboardStats.totalNewRootsToday }} hoy</v-chip>
          </v-card-title>

          <v-card-text class="px-4">
            <v-list>
              <v-list-item v-for="(detection, i) in recentRootDetections" :key="i" class="px-0 mb-3">
                <template v-slot:prepend>
                  <v-avatar color="#F0FBE6" size="36" class="mr-3">
                    <v-icon color="#7ED957" size="18">mdi-sprout</v-icon>
                  </v-avatar>
                </template>
                <div>
                  <v-list-item-title class="text-body-2 font-weight-medium">
                    {{ detection.deviceName }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    +{{ detection.newRoots }} raíces nuevas • {{ detection.growthPercentage }}% crecimiento
                  </v-list-item-subtitle>
                  <div class="d-flex align-center mt-1">
                    <v-chip size="x-small" :color="getHealthScoreColor(detection.healthScore)" text-color="white"
                      class="mr-2">
                      Score: {{ detection.healthScore }}
                    </v-chip>
                    <span class="text-caption text-medium-emphasis">{{ detection.timestamp.split(' ')[1] }}</span>
                  </div>
                </div>
              </v-list-item>
            </v-list>

            <v-btn color="primary" variant="text" block class="mt-2">Ver historial completo</v-btn>
          </v-card-text>
        </v-card>

        <!-- System Alerts -->
        <v-card flat class="rounded-lg mt-6" elevation="2">
          <v-card-title class="d-flex justify-space-between align-center px-4 py-4">
            <div class="text-h6 font-weight-bold">Alertas del Sistema</div>
            <v-btn icon variant="text" size="small">
              <v-icon>mdi-bell-outline</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="px-4">
            <v-list>
              <v-list-item v-for="(notification, i) in notifications" :key="i" class="px-0 mb-2">
                <template v-slot:prepend>
                  <v-avatar :color="notification.type === 'success' ? 'success' :
                    notification.type === 'warning' ? 'warning' :
                      notification.type === 'error' ? 'error' : 'info'" size="8" class="mr-3"></v-avatar>
                </template>
                <div>
                  <v-list-item-title class="text-body-2">{{ notification.message }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption text-medium-emphasis">
                    hace {{ notification.time }}
                  </v-list-item-subtitle>
                </div>
              </v-list-item>
            </v-list>

            <v-btn color="primary" variant="text" block class="mt-4">Ver todas las alertas</v-btn>
          </v-card-text>
        </v-card>


      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.device-row {
  transition: background-color 0.2s ease;
}

.device-row:hover {
  background-color: #f5f5f5;
}

.chart-placeholder {
  border: 1px dashed #e0e0e0;
}

.v-list-item {
  min-height: auto;
  padding: 8px 0;
}

.v-card {
  transition: box-shadow 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08) !important;
}

.device-selector {
  max-width: 300px;
}

.gap-3 {
  gap: 12px;
}
</style>
